import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import { Share2, Search, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchPosts } from "../../../../redux/blogSlice";

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,600&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

/* ─────────────────────────────────────────────────────────
   MONOCHROME LUXURY THEME  (matches News.tsx exactly)
───────────────────────────────────────────────────────── */
const C = {
  paper:   "#ffffff",
  white:   "#ffffff",
  ink:     "#0a0a0a",
  inkSoft: "#2a2a2a",
  inkMid:  "#6b6b6b",
  rule:    "#d0d0d0",
  border:  "#e0e0e0",
  surface: "#f7f7f7",
  serif:   "'Cormorant Garamond', Georgia, serif",
  sans:    "'Montserrat', sans-serif",
};

const POSTS_PER_PAGE = 8;

const MoreNews: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { posts } = useSelector((state: RootState) => state.blog);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery,  setSearchQuery]  = useState("");
  const [scrolled,     setScrolled]     = useState(false);
  const [searchFocus,  setSearchFocus]  = useState(false);
  const navigate = useNavigate();

  const page = parseInt(searchParams.get("page") || "1", 10);
  const [filteredPosts, setFilteredPosts] = useState(posts);

  useEffect(() => {
    if (!posts.length) dispatch(fetchPosts());
  }, [dispatch, posts.length]);

  useEffect(() => {
    const q = searchQuery.toLowerCase();
    setFilteredPosts(
      posts.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q) ||
          p.category?.toLowerCase().includes(q)
      )
    );
  }, [searchQuery, posts]);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const totalPages   = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const start        = (page - 1) * POSTS_PER_PAGE;
  const currentPosts = filteredPosts.slice(start, start + POSTS_PER_PAGE);

  const handlePageClick = (pg: number) => {
    setSearchParams({ page: pg.toString() });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleShare = (post: any) => {
    const url = `${window.location.origin}/${post.slug}`;
    if (navigator.share) {
      navigator.share({ title: post.title, text: post.description, url });
    } else {
      navigator.clipboard.writeText(url);
      alert("Link copied to clipboard");
    }
  };

  const year = new Date().getFullYear();

  return (
    <div style={{ minHeight:"100vh", background:C.paper, color:C.ink, fontFamily:C.sans }}>
      <style>{FONT_CSS + MORE_CSS}</style>

      {/* ════════════════════════════════════════
          HEADER
      ════════════════════════════════════════ */}
      <header style={{
        ...S.header,
        background: scrolled ? "rgba(255,255,255,0.97)" : C.paper,
        boxShadow: scrolled ? "0 1px 20px rgba(0,0,0,0.07)" : "none",
        borderBottom: `1px solid ${scrolled ? C.border : "transparent"}`,
      }}>
        {/* Back */}
        <button onClick={() => navigate("/News")} style={S.backBtn}>
          <ChevronLeft size={16} strokeWidth={1.5}/>
          <span style={S.backLabel}>Back</span>
        </button>

        {/* Wordmark */}
        <div style={S.wordmark}>
          <span style={S.wordmarkMain}>4REVA GROUP</span>
          <span style={S.wordmarkSub}>NEWS</span>
        </div>

        {/* Search bar */}
        <div style={{
          ...S.searchWrap,
          border: `1px solid ${searchFocus ? C.ink : C.border}`,
          transition: "border-color 0.2s",
        }}>
          <Search size={13} color={searchFocus ? C.ink : C.inkMid} strokeWidth={1.5}/>
          <input
            type="text"
            placeholder="Search stories…"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setSearchParams({ page: "1" }); }}
            onFocus={() => setSearchFocus(true)}
            onBlur={() => setSearchFocus(false)}
            style={S.searchInput}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} style={S.searchClear}>
              ×
            </button>
          )}
        </div>
      </header>

      {/* ════════════════════════════════════════
          PAGE MASTHEAD
      ════════════════════════════════════════ */}
      <div style={S.masthead}>
        {/* Thin top rule */}
        <div style={{ height:1, background:C.ink, width:"100%" }}/>
        <div style={S.mastheadInner}>
          <div>
            <p style={S.mastheadEyebrow}>4REVA GROUP</p>
            <h1 style={S.mastheadTitle}>All Stories</h1>
          </div>
          <div style={S.mastheadMeta}>
            <span style={S.mastheadCount}>
              {filteredPosts.length} {filteredPosts.length === 1 ? "Article" : "Articles"}
            </span>
            {searchQuery && (
              <span style={S.mastheadSearching}>
                — searching "<em>{searchQuery}</em>"
              </span>
            )}
          </div>
        </div>
        {/* Bottom rule */}
        <div style={{ height:1, background:C.border, width:"100%" }}/>
      </div>

      {/* ════════════════════════════════════════
          NEWS GRID
      ════════════════════════════════════════ */}
      <div style={S.gridSection}>
        <AnimatePresence mode="wait">
          {currentPosts.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity:0, y:12 }}
              animate={{ opacity:1, y:0 }}
              exit={{ opacity:0 }}
              style={S.emptyState}
            >
              <div style={S.emptyIcon}>∅</div>
              <p style={S.emptyTitle}>No stories found</p>
              <p style={S.emptySub}>Try adjusting your search or browse all articles</p>
              <button style={S.emptyBtn} onClick={() => setSearchQuery("")}>
                Clear Search
              </button>
            </motion.div>
          ) : (
            <motion.div
              key={`page-${page}-${searchQuery}`}
              initial={{ opacity:0 }}
              animate={{ opacity:1 }}
              exit={{ opacity:0 }}
              transition={{ duration:0.25 }}
              style={S.grid}
            >
              {currentPosts.map((post, i) => {
                const createdAt = post.createdAt
                  ? new Date(typeof post.createdAt === "string" ? post.createdAt : post.createdAt.seconds * 1000)
                  : null;

                return (
                  <motion.div
                    key={post.id}
                    initial={{ opacity:0, y:20 }}
                    animate={{ opacity:1, y:0 }}
                    transition={{ delay: (i % 4) * 0.07, duration:0.4 }}
                    style={S.card}
                    className="mn-card"
                  >
                    {/* Image */}
                    <div style={S.cardImgWrap}>
                      <img
                        src={post.featuredImage}
                        alt={post.title}
                        style={S.cardImg}
                      />
                      {/* Category badge */}
                      {post.category && (
                        <div style={S.cardBadge}>{post.category}</div>
                      )}
                      {/* Share button */}
                      <button
                        style={S.cardShareBtn}
                        onClick={() => handleShare(post)}
                        title="Share"
                      >
                        <Share2 size={13} color="#fff" strokeWidth={1.5}/>
                      </button>
                    </div>

                    {/* Body */}
                    <div style={S.cardBody}>
                      {/* Thin rule */}
                      <div style={S.cardRule}/>
                      <h3 style={S.cardTitle}>{post.title}</h3>
                      <p style={S.cardDesc}>{post.description}</p>

                      <div style={S.cardFooter}>
                        {createdAt && (
                          <span style={S.cardDate}>
                            {createdAt.toLocaleDateString("en-GB", {
                              day:"numeric", month:"short", year:"numeric"
                            })}
                          </span>
                        )}
                        <button
                          style={S.readMoreBtn}
                          onClick={() => navigate(`/${post.slug}`)}
                          className="mn-read"
                        >
                          Read More <ArrowRight size={12} style={{ marginLeft:4 }}/>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ════════════════════════════════════════
            PAGINATION
        ════════════════════════════════════════ */}
        {totalPages > 1 && (
          <div style={S.pagination}>
            {/* Prev */}
            <button
              onClick={() => page > 1 && handlePageClick(page - 1)}
              disabled={page === 1}
              style={{
                ...S.pgArrow,
                opacity: page === 1 ? 0.28 : 1,
                cursor: page === 1 ? "not-allowed" : "pointer",
              }}
            >
              <ChevronLeft size={16} strokeWidth={1.5}/>
            </button>

            {/* Page numbers */}
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => {
              const active = pg === page;
              // Show first, last, current ±1, and ellipsis
              const show = pg === 1 || pg === totalPages || Math.abs(pg - page) <= 1;
              if (!show) {
                if (pg === 2 || pg === totalPages - 1) {
                  return <span key={pg} style={S.pgEllipsis}>…</span>;
                }
                return null;
              }
              return (
                <button
                  key={pg}
                  onClick={() => handlePageClick(pg)}
                  style={{
                    ...S.pgBtn,
                    background: active ? C.ink : "transparent",
                    color:      active ? "#fff" : C.inkMid,
                    borderColor: active ? C.ink : C.border,
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {pg}
                </button>
              );
            })}

            {/* Next */}
            <button
              onClick={() => page < totalPages && handlePageClick(page + 1)}
              disabled={page === totalPages}
              style={{
                ...S.pgArrow,
                opacity: page === totalPages ? 0.28 : 1,
                cursor: page === totalPages ? "not-allowed" : "pointer",
              }}
            >
              <ChevronRight size={16} strokeWidth={1.5}/>
            </button>
          </div>
        )}

        {/* Page indicator */}
        {totalPages > 1 && (
          <p style={S.pgIndicator}>
            Page {page} of {totalPages}
          </p>
        )}
      </div>

      {/* ════════════════════════════════════════
          FOOTER
      ════════════════════════════════════════ */}
      <footer style={S.footer}>
        <div style={{ height:1, background:C.border, marginBottom:20 }}/>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
          <div style={{ fontFamily:C.serif, fontSize:16, fontWeight:600, letterSpacing:"0.12em", color:C.ink }}>
            4REVA GROUP <span style={{ color:C.inkMid }}>NEWS</span>
          </div>
          <span style={{ fontSize:12, color:C.inkMid, letterSpacing:"0.08em" }}>© 4REVA GROUP {year}</span>
          <div style={{ display:"flex", gap:20 }}>
            {["Privacy","Terms","Contact"].map(l => (
              <span
                key={l}
                style={{ fontSize:11, color:C.inkMid, cursor:"pointer", letterSpacing:"0.1em" }}
                onClick={() => navigate(`/pages/${l.toLowerCase()}`)}
              >
                {l.toUpperCase()}
              </span>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MoreNews;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {

  /* Header */
  header: {
    position: "sticky", top:0, zIndex:100,
    display: "flex", alignItems: "center",
    justifyContent: "space-between", gap: 16,
    padding: "0 28px", height: 62,
    backdropFilter: "blur(14px)",
    transition: "background 0.3s, box-shadow 0.3s, border-bottom 0.3s",
  },
  backBtn: {
    display: "flex", alignItems: "center", gap: 5,
    background: "none", border: "none", cursor: "pointer",
    color: C.inkMid, fontFamily: C.sans, fontSize: 11,
    letterSpacing: "0.1em", fontWeight: 500, flexShrink: 0,
    padding: 0,
  },
  backLabel: { display: "inline" },

  /* Wordmark */
  wordmark: {
    position: "absolute", left: "50%", transform: "translateX(-50%)",
    display: "flex", alignItems: "baseline", gap: 6,
    pointerEvents: "none",
  },
  wordmarkMain: {
    fontFamily: C.serif, fontSize: 17, fontWeight: 600,
    letterSpacing: "0.28em", color: C.ink,
  },
  wordmarkSub: {
    fontFamily: C.sans, fontSize: 9, fontWeight: 600,
    letterSpacing: "0.4em", color: C.inkMid,
  },

  /* Search */
  searchWrap: {
    display: "flex", alignItems: "center", gap: 8,
    background: C.surface, padding: "8px 14px",
    width: "min(260px, 40vw)", flexShrink: 0,
  },
  searchInput: {
    flex: 1, background: "transparent", border: "none", outline: "none",
    fontSize: 12, color: C.ink, fontFamily: C.sans,
    letterSpacing: "0.03em",
  },
  searchClear: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 16, color: C.inkMid, lineHeight: 1, padding: 0,
    flexShrink: 0,
  },

  /* Masthead */
  masthead: {
    padding: "0 40px",
    marginTop: 0,
  },
  mastheadInner: {
    display: "flex", alignItems: "flex-end",
    justifyContent: "space-between", flexWrap: "wrap",
    gap: 12, padding: "32px 0 24px",
  },
  mastheadEyebrow: {
    fontSize: 9, letterSpacing: "0.3em", color: C.inkMid,
    fontWeight: 500, fontFamily: C.sans, marginBottom: 10,
  },
  mastheadTitle: {
    fontFamily: C.serif,
    fontSize: "clamp(36px, 5vw, 64px)",
    fontWeight: 600, color: C.ink,
    lineHeight: 1, margin: 0, letterSpacing: "-0.01em",
  },
  mastheadMeta: {
    display: "flex", alignItems: "center", gap: 0,
    paddingBottom: 6,
  },
  mastheadCount: {
    fontSize: 12, color: C.inkMid, letterSpacing: "0.1em",
    fontFamily: C.sans,
  },
  mastheadSearching: {
    fontSize: 12, color: C.inkMid, fontFamily: C.sans,
    marginLeft: 8,
  },

  /* Grid section */
  gridSection: {
    maxWidth: 1300, margin: "0 auto",
    padding: "48px 40px 80px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 28,
  },

  /* Card */
  card: {
    background: C.white,
    border: `1px solid ${C.border}`,
    overflow: "hidden",
    transition: "box-shadow 0.3s, transform 0.3s",
  },
  cardImgWrap: {
    position: "relative", height: 188, overflow: "hidden",
  },
  cardImg: {
    width: "100%", height: "100%", objectFit: "cover", display: "block",
    transition: "transform 0.5s ease",
  },
  cardBadge: {
    position: "absolute", top: 12, left: 12,
    background: C.ink, color: "#fff",
    fontSize: 8, fontWeight: 700, letterSpacing: "0.22em",
    padding: "3px 9px", fontFamily: C.sans,
    textTransform: "uppercase",
  },
  cardShareBtn: {
    position: "absolute", top: 10, right: 10,
    background: "rgba(0,0,0,0.55)", border: "none",
    width: 30, height: 30, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer",
  },
  cardBody:  { padding: "20px 20px 16px" },
  cardRule:  { width: 24, height: 1.5, background: C.ink, marginBottom: 12 },
  cardTitle: {
    fontFamily: C.serif,
    fontSize: 20, fontWeight: 600, color: C.ink,
    lineHeight: 1.2, marginBottom: 9, letterSpacing: "-0.005em",
  },
  cardDesc: {
    fontSize: 12, lineHeight: 1.75, color: C.inkMid,
    fontFamily: C.sans, fontWeight: 300,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardFooter: {
    display: "flex", alignItems: "center",
    justifyContent: "space-between",
    marginTop: 16, paddingTop: 13,
    borderTop: `1px solid ${C.border}`,
  },
  cardDate: {
    fontSize: 10, color: C.inkMid, letterSpacing: "0.06em",
    fontFamily: C.sans,
  },
  readMoreBtn: {
    display: "inline-flex", alignItems: "center",
    background: "transparent", border: "none",
    color: C.ink, fontSize: 10, fontWeight: 600,
    letterSpacing: "0.14em", cursor: "pointer",
    fontFamily: C.sans, padding: 0,
    transition: "opacity 0.2s",
  },

  /* Empty state */
  emptyState: {
    textAlign: "center", padding: "80px 20px",
    display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
  },
  emptyIcon:  { fontSize: 48, color: C.border, lineHeight: 1, marginBottom: 8 },
  emptyTitle: { fontFamily: C.serif, fontSize: 28, fontWeight: 300, color: C.ink },
  emptySub:   { fontSize: 13, color: C.inkMid, fontFamily: C.sans, maxWidth: 320 },
  emptyBtn: {
    marginTop: 8, background: "transparent", border: `1px solid ${C.ink}`,
    color: C.ink, padding: "10px 28px", fontSize: 10, fontWeight: 600,
    letterSpacing: "0.18em", cursor: "pointer", fontFamily: C.sans,
  },

  /* Pagination */
  pagination: {
    display: "flex", alignItems: "center", justifyContent: "center",
    gap: 6, marginTop: 56, flexWrap: "wrap",
  },
  pgBtn: {
    width: 38, height: 38,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 12, fontFamily: C.sans, letterSpacing: "0.05em",
    border: "1px solid", cursor: "pointer",
    transition: "background 0.18s, color 0.18s",
  },
  pgArrow: {
    width: 38, height: 38,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "transparent", border: `1px solid ${C.border}`,
    color: C.inkMid,
    transition: "opacity 0.2s",
  },
  pgEllipsis: {
    width: 30, textAlign: "center",
    fontSize: 13, color: C.inkMid, letterSpacing: "0.1em",
  },
  pgIndicator: {
    textAlign: "center", marginTop: 16,
    fontSize: 10, color: C.inkMid,
    letterSpacing: "0.18em", fontFamily: C.sans,
  },

  /* Footer */
  footer: {
    background: C.paper, padding: "24px 40px",
  },
};

/* ─────────────────────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────────────────────── */
const MORE_CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { width: 0; height: 0; }

  .mn-card:hover {
    box-shadow: 0 8px 40px rgba(0,0,0,0.1);
    transform: translateY(-3px);
  }
  .mn-card:hover img { transform: scale(1.04); }
  .mn-read:hover { opacity: 0.55; }

  @media (max-width: 600px) {
    .mn-back-label { display: none; }
    header { padding: 0 16px !important; }
    div[style*="padding: 0 40px"] {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }
  }
`;